<template>
    <section class="slice barometer-home__stats">
        <div class="slice-content">
            <h2 class="slice-title joy-mb-8">{{ t('barometer.freelancer.work.stats.title', [generalBarometer.country]) }}</h2>

            <div class="barometer-home__stats__columns">
                <div class="barometer-home__stats__chart">
                    <canvas ref="pieChartCanvasRef" />
                </div>
                <div class="barometer-home__stats__legend">
                    <!-- <p>{{ t('barometer.freelancer.work.stats.legend.title') }}</p> -->
                    <div v-for="(share, i) in generalBarometer.familyAndShares" :key="i">
                        <div
                            class="barometer-home__stats__legend__chip"
                            :style="'background-color: ' + (legendItems ? legendItems[i].fillStyle : null)"
                        />
                        <NuxtLink :to="$withLocalePrefix(share.url)">{{ share.label }}</NuxtLink>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {inject, onMounted, ref} from 'vue';
    import {Chart, registerables} from 'chart.js';
    import type {ChartConfiguration, ChartData, LegendItem} from 'chart.js';
    import {onUnmounted, useTranslation} from '#imports';
    import {BAROMETER_HOME} from '~/keys';

    const {t} = useTranslation();
    const {generalBarometer} = inject(BAROMETER_HOME)!;
    const {familyAndShares} = generalBarometer;
    const pieChartCanvasRef = ref<HTMLCanvasElement | null>(null);
    const chart = ref<Chart<'pie', ChartData[]> | null>(null);
    const legendItems = ref<LegendItem[] | null>(null);

    const labels = familyAndShares?.map(({label}) => label);
    const pieData = familyAndShares?.map(({value}) => Number(value.toFixed(2)));

    function chartResize() {
        for (const id in Chart.instances) {
            Chart.instances[id].resize();
        }
    }

    onMounted(() => {
        if (pieChartCanvasRef.value) {
            const configuration: ChartConfiguration = {
                type: 'pie',
                data: {labels, datasets: [{label: '', data: pieData}]},
                options: {
                    plugins: {
                        legend: {display: false},
                    },
                },
            };
            Chart.register(...registerables);
            chart.value = new Chart(pieChartCanvasRef.value, configuration) as any;
            legendItems.value = chart.value?.legend?.legendItems as LegendItem[];
            window.addEventListener('resize', chartResize);
        }
    });

    onUnmounted(() => {
        window.removeEventListener('resize', chartResize);
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .barometer-home__stats {
        &__columns {
            position: relative;
            text-align: left;
            max-width: 500px;
            margin: 0 auto;

            @include mq.screen_sm(min) {
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                max-width: 900px;
                margin: 0 auto;
                gap: var(--joy-core-spacing-12);

                > div {
                    position: relative;
                }
            }
        }
        &__legend {
            padding-top: var(--joy-core-spacing-10);

            > div {
                display: flex;
                gap: var(--joy-core-spacing-2);
                margin-bottom: var(--joy-core-spacing-2);
            }
            a,
            a:visited {
                color: var(--joy-color-text-body);
                &:hover {
                    text-decoration: underline;
                }
            }
            &__chip {
                flex: none;
                display: inline-block;
                position: relative;
                width: var(--joy-core-spacing-7);
                height: var(--joy-core-spacing-5);
                border-radius: var(--joy-core-radius-2);
            }
        }
    }
</style>
